import { useSyncExternalStore } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthButtons } from "../features/auth/AuthButtons";
import { underlineTabClass } from "./ui/UnderlineTabs";
import { EdgeScroller } from "./EdgeScroller";
import { NAV_TABS, navTabFor } from "./navTabs";
import { navSurface, type NavSurface } from "./navSurface";

interface TopNavigationProps {
  readonly chessUsername: string | null;
  readonly platform: string | null;
  /** Opens the link-your-chess-account modal, from the account menu. */
  readonly onLinkAccount: () => void;
}

// The window is the page's scroller (Review and Play don't scroll at all, so their bar stays solid). A passive
// listener; React re-renders only when the surface it computes changes, not on every scrolled pixel.
const onScroll = (notify: () => void) => {
  window.addEventListener("scroll", notify, { passive: true });
  return () => window.removeEventListener("scroll", notify);
};

// Nav divider is deliberately stronger than the in-content hairline (--color-border, 0.06): it separates a whole
// region, and on the near-black chrome the faint hairline was invisible. Glass is the canvas at 70% under a blur, so
// whatever scrolls beneath (the landing's hero board, a page's rows) reads as behind the bar, not through it (#249).
const SURFACE: Record<NavSurface, string> = {
  clear: "bg-transparent",
  solid: "bg-canvas",
  glass:
    "bg-[color-mix(in_srgb,var(--color-bg-base)_70%,transparent)] backdrop-blur-[14px] backdrop-saturate-[1.3] shadow-[0_8px_24px_rgba(0,0,0,0.35)]",
};

export default function TopNavigation({ chessUsername, platform, onLinkAccount }: TopNavigationProps) {
  const location = useLocation();
  const surface = useSyncExternalStore(onScroll, () => navSurface({ pathname: location.pathname, scrollY: window.scrollY }));

  const active = navTabFor(location.pathname);

  return (
    <nav
      className={[
        // px tightens on mobile (px-3) → px-6 at sm+; overflow-x-hidden keeps a too-wide row from pushing the PAGE
        // wider than the viewport — the links row scrolls within itself instead (#79).
        "sticky top-0 z-[1000] flex h-[var(--nav-h)] w-full items-center px-3 sm:px-6 overflow-x-hidden border-b border-[rgba(255,255,255,0.12)]",
        "transition-[background-color,box-shadow,backdrop-filter] duration-200 ease-out motion-reduce:transition-none",
        SURFACE[surface],
      ].join(" ")}
      aria-label="Main navigation"
    >
      <Link
        to="/"
        className="mr-3 sm:mr-8 shrink-0 flex items-center no-underline transition-opacity duration-150 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
      >
        {/* The mark: the board's own two squares, 2×2. Under sm it stands alone, the wordmark is for screen readers
            only, and the tabs get the width (#262). */}
        <span aria-hidden="true" className="grid h-5 w-5 shrink-0 grid-cols-2 overflow-hidden rounded-[5px] ring-1 ring-white/[0.18] sm:mr-[9px]">
          <span className="bg-[var(--board-light)]" />
          <span className="bg-[var(--board-dark)]" />
          <span className="bg-[var(--board-dark)]" />
          <span className="bg-[var(--board-light)]" />
        </span>
        <span className="font-[family-name:var(--font-display)] text-base font-extrabold leading-none tracking-[-0.015em] text-on-surface m-0 max-sm:sr-only">
          Chess Deck
        </span>
      </Link>

      {/* The tabs take the width between the mark and the account button and scroll inside it, fading the edge they
          run past, with the page's tab scrolled into view (#262). The row never widens the page (#79). */}
      <EdgeScroller className="flex h-full min-w-0 flex-1 items-center gap-0" revealKey={location.pathname}>
        {NAV_TABS.map((t) => (
          <Link key={t.to} to={t.to} className={`${underlineTabClass(active === t.to)} max-sm:px-3`} {...(active === t.to ? { "aria-current": "page" as const } : {})}>
            {t.label}
          </Link>
        ))}
      </EdgeScroller>

      <div className="ml-3 shrink-0 flex items-center gap-3">
        <AuthButtons chessUsername={chessUsername} platform={platform} onLinkAccount={onLinkAccount} />
      </div>
    </nav>
  );
}
